<template>
  <div>
    <!-- Hero -->
    <section class="magazin-hero">
      <div class="m-container">
        <div class="magazin-hero__inner">
          <h1 class="magazin-hero__title">Magazin</h1>
          <p class="magazin-hero__desc">
            Kaufberatungen, Pflegetipps und Einrichtungsideen — Expertenwissen rund ums Einrichten.
          </p>
          <div class="magazin-hero__search">
            <MTextField
              v-model="searchQuery"
              placeholder="Artikel suchen..."
              prepend-inner-icon="magnify"
              variant="outlined"
              density="compact"
              hide-details
              single-line
              bg-color="white"
              @update:model-value="debouncedSearch"
            />
          </div>
          <div class="magazin-hero__topics">
            <MBtn
              v-for="topic in topics"
              :key="topic.value"
              :variant="activeTopic === topic.value ? 'flat' : 'text'"
              :color="activeTopic === topic.value ? 'primary' : undefined"
              :class="{ 'magazin-hero__topic--active': activeTopic === topic.value }"
              size="small"
              class="text-none magazin-hero__topic"
              @click="selectTopic(topic.value)"
            >
              {{ topic.label }}
            </MBtn>
          </div>
        </div>
      </div>
    </section>

    <div class="m-container py-8">

      <!-- Featured Artikel -->
      <template v-if="!activeTopic && !searchQuery && featuredArticles.length">
        <div class="mb-8">
          <h2 class="titel-h2 font-weight-bold mb-1">Empfohlen</h2>
          <p class="text-body-2 text-medium-emphasis mb-5">
            Die beliebtesten Artikel im Überblick
          </p>
          <div class="m-row">
            <div class="m-col m-col-12 m-col-md-7">
              <NuxtLink :to="`/magazin/${featuredArticles[0].slug}`" class="text-decoration-none">
                <MCard class="article-card-featured h-100" rounded="md" hover>
                  <MImg :src="featuredArticles[0].image || fallbackImage" :height="320" cover :lazy="false" class="featured-img">
                    <div class="featured-overlay pa-6 h-100 d-flex flex-column justify-end">
                      <MChip :color="getTopicColor(featuredArticles[0].topic)" size="small" variant="flat" class="mb-3 align-self-start text-none font-weight-bold">
                        {{ getTopicLabel(featuredArticles[0].topic) }}
                      </MChip>
                      <h2 class="text-white text-h6 font-weight-bold mb-2">{{ featuredArticles[0].title }}</h2>
                      <p class="text-white text-opacity-80 text-body-2 mb-0">{{ featuredArticles[0].excerpt }}</p>
                    </div>
                  </MImg>
                </MCard>
              </NuxtLink>
            </div>
            <div class="m-col m-col-12 m-col-md-5">
              <div class="m-row">
                <div v-for="article in featuredArticles.slice(1, 3)" :key="article.slug" class="m-col m-col-12">
                  <NuxtLink :to="`/magazin/${article.slug}`" class="text-decoration-none">
                    <MCard class="article-card-sm" rounded="md" hover>
                      <div class="d-flex">
                        <MImg :src="article.image || fallbackImage" :width="120" :height="120" cover style="flex-shrink: 0; border-radius: var(--m-radius) 0 0 var(--m-radius)" />
                        <MCardText class="pa-4">
                          <MChip :color="getTopicColor(article.topic)" size="x-small" variant="tonal" class="mb-2 text-none">
                            {{ getTopicLabel(article.topic) }}
                          </MChip>
                          <h3 class="article-title mb-1">{{ article.title }}</h3>
                          <p class="text-caption text-medium-emphasis mb-0">
                            {{ article.readTime }} min Lesezeit
                          </p>
                        </MCardText>
                      </div>
                    </MCard>
                  </NuxtLink>
                </div>
              </div>
            </div>
          </div>
        </div>
        <hr class="m-divider mb-8" />
      </template>

      <!-- Loading -->
      <template v-if="loading">
        <div class="m-row">
          <div v-for="n in 6" :key="n" class="m-col m-col-12 m-col-sm-6 m-col-md-4">
            <MSkeletonLoader type="image, heading, text" />
          </div>
        </div>
      </template>

      <!-- Alle / gefilterte Artikel -->
      <div v-else>
        <h2 class="titel-h2 font-weight-bold mb-1">
          {{ activeTopic ? getTopicLabel(activeTopic) : searchQuery ? `Suche: „${searchQuery}"` : 'Alle Artikel' }}
        </h2>
        <p class="text-body-2 text-medium-emphasis mb-5">
          {{ total }} Artikel gefunden
        </p>

        <div v-if="articles.length" class="m-row">
          <div v-for="article in articles" :key="article.slug" class="m-col m-col-12 m-col-sm-6 m-col-md-4">
            <NuxtLink :to="`/magazin/${article.slug}`" class="text-decoration-none h-100">
              <MCard class="article-card h-100 d-flex flex-column" rounded="md" hover>
                <MImg :src="article.image || fallbackImage" :height="180" cover class="article-card__img" />
                <MCardText class="pa-4 d-flex flex-column flex-grow-1">
                  <MChip :color="getTopicColor(article.topic)" size="x-small" variant="tonal" class="mb-2 text-none align-self-start">
                    {{ getTopicLabel(article.topic) }}
                  </MChip>
                  <h3 class="article-title mb-1">{{ article.title }}</h3>
                  <p class="text-body-2 text-medium-emphasis article-excerpt mb-0" style="font-size: 0.8125rem">{{ article.excerpt }}</p>
                  <div class="article-card__footer">
                    <span class="text-caption text-medium-emphasis">
                      <MIcon icon="clock-outline" :size="12" class="me-1" />
                      {{ article.readTime }} min · {{ formatDate(article.createdAt) }}
                    </span>
                  </div>
                </MCardText>
              </MCard>
            </NuxtLink>
          </div>
        </div>

        <div v-else class="text-center py-16">
          <MIcon icon="book-search" :size="64" color="var(--m-border)" class="mb-4" />
          <p class="text-h6 text-medium-emphasis">Keine Artikel gefunden</p>
          <MBtn variant="outlined" color="primary" class="mt-4 text-none" @click="resetFilters">
            Alle anzeigen
          </MBtn>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="d-flex justify-center mt-6">
        <MPagination v-model="page" :length="totalPages" @update:model-value="loadArticles" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const api = useApi();

useSeoMeta({
  title: "Magazin & Kaufberatung | Moebix",
  description: "Expertenwissen rund ums Einrichten: Kaufberatungen, Pflegetipps und Einrichtungsideen für dein Zuhause.",
  ogTitle: "Moebix Magazin — Kaufberatung & Wohnideen",
  ogDescription: "Expertenwissen rund ums Einrichten: Kaufberatungen, Pflegetipps und Einrichtungsideen für dein Zuhause.",
  ogType: "website",
  ogImage: "/moebix_og_sticker.jpg",
  ogUrl: "https://www.moebix.de/magazin",
});

useHead({
  link: [{ rel: "canonical", href: "https://www.moebix.de/magazin" }],
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Moebix Magazin",
        description: "Kaufberatung, Pflegetipps und Einrichtungsideen rund um Möbel.",
        url: "https://www.moebix.de/magazin",
        publisher: {
          "@type": "Organization",
          name: "Moebix",
          url: "https://www.moebix.de",
          logo: "https://www.moebix.de/moebix_logo.png",
        },
      }),
    },
  ],
});

const fallbackImage = "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&q=80";

const searchQuery = ref("");
const activeTopic = ref("");
const page = ref(1);
const loading = ref(false);

const articles = ref<any[]>([]);
const total = ref(0);
const totalPages = ref(1);
const featuredArticles = ref<any[]>([]);

const topics = [
  { value: "", label: "Alle", icon: "view-grid" },
  { value: "kaufberatung", label: "Kaufberatung", icon: "clipboard-check" },
  { value: "einrichtung", label: "Einrichtung", icon: "floor-lamp" },
  { value: "pflege", label: "Pflege & Tipps", icon: "spray" },
  { value: "trends", label: "Trends", icon: "star" },
];

const topicMap: Record<string, { label: string; color: string }> = {
  kaufberatung: { label: "Kaufberatung", color: "primary" },
  einrichtung: { label: "Einrichtung", color: "info" },
  pflege: { label: "Pflege & Tipps", color: "success" },
  trends: { label: "Trends", color: "secondary" },
};

const getTopicLabel = (topic: string) => topicMap[topic]?.label ?? topic;
const getTopicColor = (topic: string) => topicMap[topic]?.color ?? "primary";

const formatDate = (d: string) => {
  const date = new Date(d);
  return date.toLocaleDateString("de-DE", { month: "short", year: "numeric" });
};

let debounceTimer: ReturnType<typeof setTimeout>;
const debouncedSearch = () => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => { page.value = 1; loadArticles(); }, 300);
};

function selectTopic(value: string) {
  activeTopic.value = value;
  page.value = 1;
  loadArticles();
}

function resetFilters() {
  activeTopic.value = "";
  searchQuery.value = "";
  page.value = 1;
  loadArticles();
}

async function loadArticles() {
  loading.value = true;
  try {
    const perPage = 12;
    const params: any = { limit: perPage, offset: (page.value - 1) * perPage };
    if (activeTopic.value) params.topic = activeTopic.value;
    if (searchQuery.value) params.search = searchQuery.value;

    // API-Shape: { items, total, limit, offset } — kein `articles`-Feld.
    const data = await api<any>("/api/magazine", { params });
    articles.value = Array.isArray(data?.items) ? data.items : [];
    total.value = typeof data?.total === "number" ? data.total : 0;
    totalPages.value = Math.max(1, Math.ceil(total.value / perPage));
  } catch {
    articles.value = [];
    total.value = 0;
    totalPages.value = 1;
  } finally {
    loading.value = false;
  }
}

async function loadFeatured() {
  try {
    const data = await api<any>("/api/magazine", { params: { featured: "true", limit: 3 } });
    featuredArticles.value = Array.isArray(data?.items) ? data.items : [];
  } catch {
    featuredArticles.value = [];
  }
}

onMounted(() => {
  loadArticles();
  loadFeatured();
});
</script>

<style scoped>
.magazin-hero {
  background: var(--m-surface);
  border-bottom: 1px solid var(--m-border-light);
  padding: 32px 0 0;
}

.magazin-hero__inner {
  max-width: 600px;
}

.magazin-hero__title {
  font-family: "Justtellmewhat", sans-serif;
  font-size: 1.75rem;
  font-weight: 700;
  font-style: italic;
  color: var(--m-secondary);
  margin-bottom: 4px;
}

.magazin-hero__desc {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.55);
  margin-bottom: 16px;
  line-height: 1.5;
}

.magazin-hero__search {
  max-width: 360px;
  margin-bottom: 16px;
}

.magazin-hero__topics {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding-bottom: 0;
}

.magazin-hero__topic {
  color: rgba(0, 0, 0, 0.55) !important;
  border-bottom: 2px solid transparent;
  border-radius: 0 !important;
  padding: 8px 12px 10px !important;
  min-width: auto !important;
}

.magazin-hero__topic--active {
  color: var(--m-primary) !important;
  background: transparent !important;
  border-bottom-color: var(--m-primary);
}

.featured-overlay {
  background: linear-gradient(to top, rgba(0, 0, 0, 0.75) 0%, transparent 60%);
}

.featured-img {
  border-radius: inherit;
}

.article-card__img {
  height: 180px !important;
  overflow: hidden;
}

.article-card__img :deep(img) {
  object-fit: cover !important;
  width: 100% !important;
  height: 100% !important;
}

.article-card {
  transition: transform 0.2s ease;
  border: 1px solid transparent;
}

.article-card:hover {
  transform: translateY(-3px);
  border-color: rgba(60, 143, 147, 0.2);
}

.article-card__footer {
  margin-top: auto;
  padding-top: 12px;
}

.article-card-featured {
  overflow: hidden;
}

.article-card-sm {
  transition: transform 0.2s ease;
  border: 1px solid transparent;
}

.article-card-sm:hover {
  transform: translateY(-2px);
  border-color: rgba(60, 143, 147, 0.2);
}

.article-title {
  font-family: "Play", sans-serif;
  font-size: 0.9375rem;
  font-weight: 700;
  line-height: 1.35;
  color: rgba(0, 0, 0, 0.85);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  height: calc(2 * 1.35em);
}

.article-excerpt {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.text-opacity-80 {
  opacity: 0.8;
}
</style>
